import React, { useEffect } from 'react';
import { ConfigProvider, App as AntdApp } from 'antd';
import enUS from 'antd/locale/en_US';
import { createAppTheme } from '../theme';
import {
  applyColorModeToDocument,
  COLOR_MODE_STORAGE_KEY,
  getSystemColorMode,
} from '../theme/themeMode';
import { applyDesignTokens } from '../design/tokenCssVars';
import { useThemeStore } from '../theme/themeStore';
import { setToastMessage } from '../lib/toast';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ToastBridge: React.FC = () => {
  const { message } = AntdApp.useApp();

  useEffect(() => {
    setToastMessage(message);
  }, [message]);

  return null;
};

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const resolvedMode = useThemeStore((s) => s.resolvedMode);
  const storedMode = useThemeStore((s) => s.storedMode);
  const syncFromStorage = useThemeStore((s) => s.syncFromStorage);

  useEffect(() => {
    applyColorModeToDocument(resolvedMode);
    applyDesignTokens(resolvedMode);
  }, [resolvedMode]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === COLOR_MODE_STORAGE_KEY) {
        syncFromStorage();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [syncFromStorage]);

  useEffect(() => {
    if (storedMode !== null) {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const systemMode = getSystemColorMode();
      applyColorModeToDocument(systemMode);
      useThemeStore.setState({ resolvedMode: systemMode });
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [storedMode]);

  const appTheme = createAppTheme(resolvedMode);

  return (
    <ConfigProvider
      locale={enUS}
      theme={appTheme}
    >
      <AntdApp
        message={{
          top: 64,
          maxCount: 3,
          duration: 3,
        }}
      >
        <ToastBridge />
        {children}
      </AntdApp>
    </ConfigProvider>
  );
};

export default ThemeProvider;
